import React, { useState } from "react";
import {
  Grid,
  Container,
  Typography,
  TextField,
  Select,
  MenuItem,
} from "@mui/material";
import { CustomTitle } from "../../components/Custom/CustomTypography";
import api from "../../globals/axios";
import CustomAppBar from "../../components/AppBar";
import { useTranslation } from "react-i18next";
import CustomButton from "../../components/Custom/CustomButton";
import useFormValidation from "../../components/Custom/FormValidation";
import { createExternalSoilTestValidationSchema } from "../../components/Custom/ValidationSchema";
import { useAlert } from "../../components/Alert";
import CustomTextField from "../../components/Custom/CustomTextField";
import { getExternalSoilTestFields } from "../../Data/fieldData";
import { useNavigate } from "react-router-dom";
import { isNotNullOrEmptyArray } from "../../globals/utils";
import LoaderContainer from "../../components/LoaderContainer";

const validationSchema = createExternalSoilTestValidationSchema();

const FormTextField = ({ label, name, value, onChange, error, disabled }) => {
  return (
    <TextField
      label={label}
      name={name}
      value={value}
      onChange={(e) => onChange(name, e.target.value)}
      required
      fullWidth
      disabled={disabled}
      sx={{
        marginTop: "20px",
      }}
      error={Boolean(error)}
      helperText={error}
    />
  );
};

const CreateExternalSoilReport = () => {
  const [formData, setFormData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [locLoading, setLocLoading] = useState(false);
  const [postOfficeOptions, setPostOfficeOptions] = useState([]);
  const { t } = useTranslation();
  const { errors, validate } = useFormValidation(formData, validationSchema);
  const { showAlert } = useAlert();
  const navigate = useNavigate();

  const handleChange = (name, value) => {
    if (name == "pincode") {
      if (value.length === 6) {
        setLocLoading(true);
        api
          .get(`common/pincode/${value}`)
          .then((response) => {
            const postOfficesData = response.data[0]?.PostOffice;
            if (postOfficesData && postOfficesData.length > 0) {
              const postOffices = postOfficesData.map(
                (postOffice) => postOffice.Name
              );
              setPostOfficeOptions(postOffices);
              setFormData((prevData) => ({
                ...prevData,
                state: postOfficesData[0].State,
                district: postOfficesData[0].District,
              }));
            }
          })
          .catch((error) => {
            console.error("Error Fetching Post Offices:", error);
            showAlert("error", "Failed to load location data");
          })
          .finally(() => {
            setLocLoading(false);
          });
      }
    }
    setFormData((prevData) => ({ ...prevData, [name]: value }));
    validate({ ...formData, [name]: value });
  };

  const handleCreation = () => {
    validate(formData);
    if (Object.keys(errors).length > 0) {
      alert("Please fix the validation errors before submitting.");
      return;
    }
    const _formData = new FormData();
    for (const key in formData) {
      _formData.append(key, formData[key]);
    }
    _formData.append("source", "EXTERNAL");
    const apiRequest = api.post(
      "/soil-testing/create-external-soil-test-report/",
      _formData
    );

    setIsLoading(true);
    apiRequest
      .then((response) => {
        if (response && response.data) {
          const { id } = response.data;
          showAlert("success", "Generated Successfully");
          navigate(`/external-soil-report/${id}`);
        } else {
          showAlert("error", "Error creating soil test");
        }
      })
      .catch((error) => {
        console.log(error);
        const { response } = error || {};
        const { data } = response || {};
        if (isNotNullOrEmptyArray(data)) {
          showAlert("error", data[0]);
          return;
        }
        showAlert("error", "Error creating soil test");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  if (isLoading) {
    return <LoaderContainer loading={isLoading} />;
  }

  return (
    <>
      <CustomAppBar />
      <Container maxWidth="xs">
        <div>
          <Grid
            container
            justify="center"
            direction="column"
            style={{ marginTop: "10px" }}
          >
            <CustomTitle>{t("Add Soil Test")}</CustomTitle>
            {getExternalSoilTestFields(t).map((field) => (
              <CustomTextField
                key={field.name}
                label={field.label}
                name={field.name}
                value={formData[field.name] || ""}
                onChange={handleChange}
                type={field.type}
                options={field.options}
                error={errors[field.name]}
                helperText={errors[field.name]}
              />
            ))}

            {locLoading ? (
              <Typography>{`${t("Loading")}...`}</Typography>
            ) : (
              <>
                <FormTextField
                  label={t("State")}
                  name="state"
                  value={formData.state || ""}
                  onChange={handleChange}
                  error={errors.state}
                  disabled
                />
                <FormTextField
                  label={t("District")}
                  name="district"
                  value={formData.district || ""}
                  onChange={handleChange}
                  error={errors.district}
                  disabled
                />
                <Select
                  name="postoffice"
                  value={formData.postoffice || ""}
                  onChange={(e) => handleChange("postoffice", e.target.value)}
                  fullWidth
                  sx={{
                    marginTop: "20px",
                  }}
                  placeholder="Post Office"
                  error={Boolean(errors.postoffice)}
                  helperText={
                    errors.postoffice || "Add Pincode to get post offices"
                  }
                  disabled={postOfficeOptions.length === 0}
                >
                  {postOfficeOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </>
            )}

            <FormTextField
              label={t("Address Line 1")}
              name="address_line1"
              value={formData.address_line1}
              onChange={handleChange}
              error={errors.address_line1}
            />

            <FormTextField
              label={t("Village")}
              name="village"
              value={formData.village}
              onChange={handleChange}
              error={errors.village}
            />

            <CustomButton onClick={handleCreation}>
              {t("Generate Report")}
            </CustomButton>
          </Grid>
        </div>
      </Container>
    </>
  );
};

export default CreateExternalSoilReport;
